// useChains — ONE chain wire for every level (ADR-026 §C): the chains at a
// scope (`GET …/<plural>/:id/responsibilities`) and the whole-chain PUT
// (`…/responsibilities/:rid/assignments` — the backing consent travels as
// `grant_backing`: ADR-052 §5 the scope grant, ADR-083 §3 the team membership;
// the place's stamp as `row_version`, ADR-084 §4 — a chain is a set beneath it).
// A level joins by its `ChainLevel` member and a line in each of the two maps
// (`CHAIN_PATHS` · `ENTITY_PREFIX`) once its route lands (project · warehouse ·
// vehicle_pool are the catalogue's, not routed yet). The chains live under their
// own key family; a save invalidates the scope's chains and the scope entity's own
// prefix (its list's coverage cell, its detail with the moved stamp — a team's
// members after a consent), both awaited — the save resolves once they are read
// again — and the people (their chains); a consent also forgets what the person
// pickers met (a row's backed/member fact changed under them).

import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { SetAssignmentChainRequest } from '@ems-app/schemas/accountability/requests';
import type { ScopeChainsResponse } from '@ems-app/schemas/accountability/responses';
import { apiClient } from '../lib/apiClient.js';
import { clustersKeys } from './useClusters.js';
import { forgetLookupRows } from './useLookup.js';
import { sitesKeys } from './useSites.js';
import { teamsKeys } from './useTeams.js';
import { usersKeys } from './useUsers.js';
import { zonesKeys } from './useZones.js';

/** The levels whose chain routes are live — a subset of the catalogue's `RESPONSIBILITY_LEVELS`. */
export type ChainLevel = 'zone' | 'cluster' | 'site' | 'team';
export type ChainScopeId = number | string;

const CHAIN_PATHS: Record<ChainLevel, string> = { zone: 'zones', cluster: 'clusters', site: 'sites', team: 'teams' };
const ENTITY_PREFIX: Record<ChainLevel, (projectId: string) => readonly unknown[]> = { zone: zonesKeys.all, cluster: clustersKeys.all, site: sitesKeys.all, team: teamsKeys.all };

export const chainsKeys = {
  /** Every scope's chains — the prefix a write that moves a person across places invalidates (the handover, the leave). */
  all: (projectId: string) => ['chains', projectId] as const,
  scope: (projectId: string, level: ChainLevel, id: ChainScopeId) => ['chains', projectId, level, String(id)] as const,
};

/** `/projects/:p/<plural>/:id/responsibilities` — the level's segment from the one map. */
export function chainsPath(projectId: string, level: ChainLevel, id: ChainScopeId): string {
  return `/projects/${projectId}/${CHAIN_PATHS[level]}/${id}/responsibilities`;
}

/** The chains at a scope, with the PLACE's stamp the editor's save carries back (`row_version` — read with the
 *  chains, ADR-084 §3). `quiet` — the chain drawer's Load the latest: a place gone meanwhile answers the drawer, never
 *  the not-found page (ADR-084 §6). */
export async function fetchScopeChains(projectId: string, level: ChainLevel, id: ChainScopeId, quiet = false): Promise<ScopeChainsResponse> {
  return (await apiClient.get<ScopeChainsResponse>(chainsPath(projectId, level, id), quiet ? { quietNotFound: true } : undefined)).data;
}

/** The chains at ONE scope (ids only — a host names the few people through the person lookup). Mount only while the
 *  field-force module is on (the route 403s otherwise); `id` null = nothing asked. */
export function useScopeChains(projectId: string, level: ChainLevel, id: ChainScopeId | null): UseQueryResult<ScopeChainsResponse, Error> {
  const asked = id !== null && id !== 0 && String(id).length > 0;
  return useQuery({
    queryKey: chainsKeys.scope(projectId, level, asked ? id : ''),
    queryFn: () => fetchScopeChains(projectId, level, id as ChainScopeId),
    enabled: projectId.length > 0 && asked,
  });
}

export interface SetAssignmentChainVars {
  level: ChainLevel;
  scopeId: ChainScopeId;
  responsibilityId: number;
  /** The DESIRED ordered ids (position 1 = lead); the server diffs and renumbers. */
  userIds: string[];
  /** The backing consent — false on a first save; the shared save flow re-PUTs with it after the 409 confirm. */
  grantBacking?: boolean;
  /** The PLACE's stamp the editor opened with — the chain read's `row_version` (ADR-084 §4 — a set beneath its place). */
  rowVersion?: number;
}

/** PUT the whole chain at a scope. Errors bubble (the save flow reads the 409, the host maps the code). */
export function useSetAssignmentChain(projectId: string): UseMutationResult<void, Error, SetAssignmentChainVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ level, scopeId, responsibilityId, userIds, grantBacking = false, rowVersion }) => {
      const body: SetAssignmentChainRequest = { userIds, grant_backing: grantBacking, row_version: rowVersion };
      await apiClient.put(`${chainsPath(projectId, level, scopeId)}/${responsibilityId}/assignments`, body);
    },
    onSuccess: async (_data, { level, scopeId, grantBacking }) => {
      if (grantBacking) forgetLookupRows(qc, projectId, 'users');
      void qc.invalidateQueries({ queryKey: usersKeys.all(projectId) });
      // The place too is awaited: the save moved its stamp, and the drawer's next chain save carries the new one.
      await Promise.all([qc.invalidateQueries({ queryKey: ENTITY_PREFIX[level](projectId) }), qc.invalidateQueries({ queryKey: chainsKeys.scope(projectId, level, scopeId) })]);
    },
  });
}
